import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import { platformSend, setCsrfToken, errorOf } from './platformApi';
import AuthShell from '@pages/auth/AuthShell';

/**
 * P01 — platform sign-in. Password first; then either the authenticator step (enrolled
 * admins) or straight to TOTP setup (first sign-in), which is the only thing a
 * password-only session may do. No tenant/company login lives here.
 */
export default function PlatformLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ticket, setTicket] = useState(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const afterLogin = (res) => {
    setCsrfToken(res.csrfToken);
    nav(res.needsTotpEnrollment ? '/platform/setup-2fa' : ((loc.state && loc.state.from) || '/platform'), { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const res = await platformSend('post', '/auth/login', { email, password });
      setPassword('');
      if (res.mfaRequired) setTicket(res.ticket);
      else afterLogin(res);
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      afterLogin(await platformSend('post', '/auth/mfa', { ticket, code }));
    } catch (ex) {
      const { message, status } = errorOf(ex);
      setErr(message);
      if (status === 401 && /expired/i.test(message)) setTicket(null);
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  return (
    <AuthShell
      variant="platform"
      title="Platform console"
      subtitle="For platform operators. Company users sign in at their company's address."
      legalLinks={[]}
    >
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {!ticket ? (
          <form className="space-y-3" onSubmit={submitPassword}>
            <Input label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Signing in…' : 'Continue'}</Button>
          </form>
        ) : (
          <form className="space-y-3" onSubmit={submitCode}>
            <p className="text-sm text-secondary-600">Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p>
            <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus required />
            <Button type="submit" className="w-full" disabled={busy || !code.trim()}>{busy ? 'Verifying…' : 'Sign in'}</Button>
            <Button type="button" variant="secondary" className="w-full" onClick={() => { setTicket(null); setErr(''); }}>Use a different account</Button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
